import React from "react";
import { observer } from "mobx-react";
import { Box, Paper, useTheme } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import { PodRevenueDashboardStore } from "../../../../stores/dashboards/PodRevenueDashboardStore";
import { PodRevenueKpis as PodRevenueKpisModel } from "../../../../../../models/Dashboard/PodRevenue/PodRevenueSummary";
import {
  formatPodCount,
  formatPodMoney,
  formatPodShare,
} from "./podRevenueFormat";
import {
  PodRevenueEmptyState,
  PodRevenueHint,
  PodRevenueSectionLabel,
  PodRevenueSkeletonCard,
} from "./PodRevenueSection";

export type PodKpiTarget = "products" | "lines";

interface Props {
  store: PodRevenueDashboardStore;
  onSelect: (target: PodKpiTarget) => void;
}

type Kpis = PodRevenueKpisModel;

const tiles: Array<{
  key: string;
  label: string;
  hint: string;
  target?: PodKpiTarget;
  cta?: string;
  value: (k: Kpis) => string;
  share?: (k: Kpis) => number | null | undefined;
}> = [
  {
    key: "gross",
    label: "Gross revenue",
    hint: "Total billed: selling + shipping + handling, all with GST",
    // target: "trend", // weekly gross trend hidden for now
    // cta: "View trend",
    value: (k) => formatPodMoney(k.gross_revenue),
  },
  {
    key: "net",
    label: "Net before GST",
    hint: "Selling + shipping + handling before any GST",
    value: (k) => formatPodMoney(k.net_before_gst),
    share: (k) => k.net_before_gst,
  },
  {
    key: "gst",
    label: "GST",
    hint: "Total tax on selling + shipping + handling",
    value: (k) => formatPodMoney(k.gst_amount),
    share: (k) => k.gst_amount,
  },
  {
    key: "selling",
    label: "Selling (excl. GST)",
    hint: "Product selling total without GST",
    value: (k) => formatPodMoney(k.selling_exclusive),
    share: (k) => k.selling_exclusive,
  },
  {
    key: "shipping",
    label: "Shipping (excl. GST)",
    hint: "POD shipping charge without GST",
    value: (k) => formatPodMoney(k.shipping_amount),
    share: (k) => k.shipping_amount,
  },
  {
    key: "handling",
    label: "Handling (excl. GST)",
    hint: "POD handling charge without GST",
    value: (k) => formatPodMoney(k.handling_amount),
    share: (k) => k.handling_amount,
  },
  {
    key: "orders",
    label: "Orders",
    hint: "POD orders in this date range (not cancelled)",
    target: "lines",
    cta: "View lines",
    value: (k) => formatPodCount(k.order_count),
  },
  {
    key: "units",
    label: "Units",
    hint: "Total product quantity across lines",
    target: "products",
    cta: "View products",
    value: (k) => formatPodCount(k.unit_count),
  },
  {
    key: "aov",
    label: "Avg order gross",
    hint: "Gross revenue divided by order count",
    target: "lines",
    cta: "View lines",
    value: (k) => formatPodMoney(k.avg_order_gross),
  },
];

const grid = {
  display: "grid",
  gridTemplateColumns: {
    xs: "1fr",
    sm: "repeat(2, minmax(0, 1fr))",
    md: "repeat(3, minmax(0, 1fr))",
    lg: "repeat(5, minmax(0, 1fr))",
  },
  gap: 1.25,
};

const PodRevenueKpis: React.FC<Props> = observer(({ store, onSelect }) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  const tone: Record<string, string> = {
    gross: colors.darkBlue,
    selling: colors.darkBlue,
    net: colors.purple,
    gst: theme.palette.goldenYellow.main,
    handling: colors.successGreen,
    shipping: colors.purple,
    orders: colors.navyText,
    units: colors.navyText,
    aov: colors.navyText,
  };

  if (store.summaryError) {
    return (
      <>
        <PodRevenueSectionLabel label="Revenue snapshot" />
        <PodRevenueEmptyState
          error
          message={store.summaryError}
          onRetry={() => void store.refresh()}
        />
      </>
    );
  }

  if (!store.summary) {
    if (!store.summaryLoading) return null;
    return (
      <>
        <PodRevenueSectionLabel label="Revenue snapshot" />
        <Box sx={grid}>
          {tiles.map((t) => (
            <PodRevenueSkeletonCard key={t.key} height={84} />
          ))}
        </Box>
      </>
    );
  }

  const kpis = store.summary.data.kpis;
  const gross = kpis.gross_revenue;

  return (
    <>
      <PodRevenueSectionLabel
        label="Revenue snapshot"
        hint="Excl. GST view — Selling, Shipping, and Handling are without tax. GST is the full tax total. Gross is the all-in billed amount."
      />
      <Box sx={grid}>
        {tiles.map((tile) => {
          const isHero = tile.key === "gross";
          const target = tile.target;
          const clickable = !!target;
          const accent = tone[tile.key];
          const select = () => target && onSelect(target);
          const share = tile.share ? tile.share(kpis) : null;
          return (
            <Paper
              key={tile.key}
              variant="outlined"
              role={clickable ? "button" : undefined}
              tabIndex={clickable ? 0 : undefined}
              onClick={clickable ? select : undefined}
              onKeyDown={(e) => {
                if (clickable && (e.key === "Enter" || e.key === " ")) {
                  e.preventDefault();
                  select();
                }
              }}
              sx={{
                borderRadius: 1.5,
                cursor: clickable ? "pointer" : "default",
                position: "relative",
                overflow: "hidden",
                px: 1.5,
                py: 1.25,
                borderColor: colors.softCardBorder,
                bgcolor: isHero ? colors.lightBlue : colors.softCardBackground,
                boxShadow: "none",
                transition:
                  "transform .15s ease, box-shadow .15s ease, border-color .15s ease",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 3,
                  bgcolor: accent,
                  opacity: isHero ? 1 : 0.35,
                  transition: "opacity .15s ease",
                },
                "&:hover, &:focus-visible": clickable
                  ? {
                      borderColor: accent,
                      transform: "translateY(-1px)",
                      boxShadow: "0 6px 18px rgba(15, 23, 42, 0.08)",
                      outline: "none",
                    }
                  : {},
                "&:hover::before": { opacity: clickable ? 1 : undefined },
                "&:hover .pod-kpi-cta, &:focus-visible .pod-kpi-cta": {
                  opacity: 1,
                  transform: "translateX(0)",
                },
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap={1}
              >
                <CustomTypography
                  variant="heading8"
                  fontWeight={600}
                  color={colors.mutedSlateText}
                >
                  {tile.label}
                </CustomTypography>
                <PodRevenueHint text={tile.hint} />
              </Box>
              <CustomTypography
                variant={isHero ? "heading4" : "heading5"}
                fontWeight={700}
                color={colors.navyText}
                sx={{
                  mt: 0.25,
                  lineHeight: 1.3,
                  letterSpacing: "-0.02em",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {tile.value(kpis)}
              </CustomTypography>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mt={0.5}
                gap={1}
              >
                <CustomTypography
                  variant="heading8"
                  fontWeight={400}
                  color={colors.greyTextColor}
                >
                  {share != null
                    ? `${formatPodShare(share, gross)} of gross`
                    : " "}
                </CustomTypography>
                {tile.cta && (
                  <Box
                    className="pod-kpi-cta"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                      color: accent,
                      opacity: 0,
                      transform: "translateX(-4px)",
                      transition: "all .15s ease",
                      fontSize: "0.68rem",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {tile.cta}
                    <ArrowForwardIcon sx={{ fontSize: 12 }} />
                  </Box>
                )}
              </Box>
            </Paper>
          );
        })}
      </Box>
    </>
  );
});

export default PodRevenueKpis;
